import { CORE_VALUES, LEADERSHIP, IMAGES } from "../lib/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQS = [
  { q: "Who is eligible for the mentoring program?", a: "Any young man between the ages of 10 and 17 who lives in or attends school in Clayton County, GA. Referrals from schools, courts, and families are all welcome." },
  { q: "Is there a cost to participate?", a: "No. All programming is completely free. Meals, transportation, and materials are provided." },
  { q: "How do parents get involved?", a: "Parents attend monthly family sessions, receive weekly progress notes, and can request 1:1 case management support." },
  { q: "Are you faith-based?", a: "We are faith-informed and inclusive. Faith & character workshops are voluntary; every young man is welcome regardless of background." },
  { q: "How can my church, school, or business partner with you?", a: "Visit our Partners page or use the Contact form — we love new partnerships that serve young men." },
];

export default function About() {
  return (
    <>
      <section className="bg-[#FDFBF7] border-b border-black/10">
        <div className="container-x py-20 md:py-28 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-7">
            <span className="overline text-[#6B21A8]">About Us</span>
            <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold tracking-tight leading-none">Our story is<br /><span className="italic">their story.</span></h1>
            <p className="mt-8 max-w-xl text-black/70 text-lg leading-relaxed">
              The Change Center Mentoring Group was founded in Clayton County, Georgia by a coalition of pastors, coaches, teachers, and parents who refused to accept the status quo for boys in our community. We started with a small circle in a church basement. Today, we serve hundreds — and we're just getting started.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col items-center md:items-start">
            <div className="relative w-full max-w-md overflow-hidden border border-black/10">
              <img
                src="/photos/grand-opening.jpg"
                alt="Change Center Mentoring Grand Opening"
                className="w-full h-auto object-cover"
              />
            </div>
            <p className="mt-3 text-xs uppercase tracking-widest text-black/50">Grand Opening — Change Center Mentoring HQ, Forest Park GA.</p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-white border-b border-black/10">
        <div className="container-x py-20 grid gap-12 md:grid-cols-3">
          <div>
            <p className="overline text-[#6B21A8]">Mission</p>
            <p className="mt-4 font-display text-2xl leading-snug">Changing this generation one young man at a time — through mentorship, education, workforce development, faith, and life skills.</p>
          </div>
          <div>
            <p className="overline text-[#6B21A8]">Vision</p>
            <p className="mt-4 font-display text-2xl leading-snug">A generation of young men equipped to lead their families, their careers, and their communities with integrity and confidence.</p>
          </div>
          <div>
            <p className="overline text-[#6B21A8]">How</p>
            <p className="mt-4 font-display text-2xl leading-snug">Weekly small groups, monthly community service, quarterly retreats, and 1:1 mentorship — all wrapped in brotherhood.</p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="noise" />
        <div className="container-x py-20 md:py-28">
          <span className="overline text-[#F5B90A]">Core Values</span>
          <h2 className="mt-3 font-display text-4xl md:text-6xl font-bold">Six words that guide us.</h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-white/10">
            {CORE_VALUES.map((v) => (
              <div key={v.name} className="border-b border-r border-white/10 p-8 md:p-10 last:border-r-0">
                <p className="font-display text-4xl font-bold text-[#F5B90A]">{v.name}</p>
                <p className="mt-3 text-white/70">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-white">
        <div className="container-x py-20 md:py-28">
          <span className="overline text-[#6B21A8]">Leadership Team</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">The people carrying this forward.</h2>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {LEADERSHIP.map((p) => (
              <div key={p.name} className="border border-black/10 p-8 hover:shadow-[4px_4px_0_#0A0A0A] transition-shadow" data-testid={`about-leader-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {p.photo ? (
                  <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-full object-cover object-top ring-2 ring-[#F5B90A]" data-testid="about-leader-photo" />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-[#6B21A8] text-white font-display text-2xl font-bold flex items-center justify-center" data-testid="about-leader-initials">
                    {p.name.replace(/^(Pastor|Dr\.|Coach|Ms\.)\s+/, "").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                )}
                <p className="mt-6 font-display text-xl font-semibold">{p.name}</p>
                <p className="text-sm text-black/60 mt-1">{p.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDFBF7] border-t border-black/10">
        <div className="container-x py-20 md:py-28 grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="overline text-[#6B21A8]">FAQ</span>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">Common questions from families and partners.</h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-black/15">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display text-lg">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-black/70 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
}
